'use client';

import * as React from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { initialTableFilters, languages } from '@/app/constants';
import { useAppSelector } from '@/store/hooks';
import { getDrawerContent } from '@/store/reducers/drawer-content-slice';
import { hideDrawerState } from '@/store/reducers/ui-slice';
import { BonusStatus } from '@alien-platform/bo-sdk';
import { BonusPrizeType, BonusType } from '@ap-db-worker/common';
import { zodResolver } from '@hookform/resolvers/zod';
import { Accordion, AccordionDetails, AccordionSummary, Button, Grid, Stack, Typography } from '@mui/material';
import { CaretDown } from '@phosphor-icons/react/dist/ssr/CaretDown';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { z as zod } from 'zod';

import { AllFilters, BetMode, FieldType, GeneralDrawer, InputType, TableFilter } from '@/types/general';
import { capitalizeString } from '@/lib/helpers';

import { CustomConfigInput } from './custom-config-input';

const schema = zod.object({
  playerCode: zod.string().optional(),
  playerId: zod.string().max(255).optional(),
  email: zod.string().email('Must be a valid email').max(255).optional().or(zod.literal('')).nullable(),
  filterBy: zod.string().optional(),
  filterValue: zod.string().optional(),
  cfAccountId: zod.string().optional(),
  cfAccountCode: zod.string().optional(),
  bonusId: zod.string().optional(),
  status: zod.string().optional(),
  bonusDefinitionType: zod.string().optional(),
  prizeType: zod.string().optional(),
  visibility: zod.string().optional(),
  telegramUsername: zod.string().optional(),
  affId: zod.string().optional(),
  registrationTimeFrom: zod.string().optional(),
  registrationTimeTo: zod.string().optional(),
  depositFrom: zod.string().optional(),
  depositTo: zod.string().optional(),
});

type Values = zod.infer<typeof schema>;

const defaultValues = {
  bonusId: '',
  playerCode: '',
  playerId: '',
  email: '',
  filterBy: '',
  filterValue: '',
  cfAccountId: '',
  cfAccountCode: '',
  status: '',
  bonusDefinitionType: '',
  prizeType: '',
  visibility: '',
  telegramUsername: '',
  affId: '',
  registrationTimeFrom: '',
  registrationTimeTo: '',
  depositFrom: '',
  depositTo: '',
} satisfies Values;

function TableFilters(): React.JSX.Element {
  const { TABLE_FILTERS: tableFilter } = useAppSelector(getDrawerContent);
  //   const [filters, setFilters] = React.useState<AllFilters>({ ...initialTableFilters });
  const filters: AllFilters = { ...initialTableFilters };
  const searchParams = useSearchParams();
  const dispatch = useDispatch();
  const router = useRouter();
  //   let initialVisibleFilters: string[] = [];
  //   let initialHiddenFilters: string[] = [];
  //   let fromDateLabel = 'Opened At From';

  const {
    control,
    handleSubmit,
    reset,
    // formState: { errors },
  } = useForm<Values>({ defaultValues, resolver: zodResolver(schema) });

  const initialFilterConfig = React.useMemo(() => {
    let statusItems: { label: string; value: string; name?: string }[] | undefined;
    let bonusTypeItems: { label: string; value: string; name?: string }[] | undefined;
    let prizeTypeItems: { label: string; value: string; name?: string }[] | undefined;
    let visibilityMenuItemList: { label: string; value: string; name?: string }[] | undefined;

    switch (tableFilter) {
      case TableFilter.PLAYERS:
      case TableFilter.PLAYERS_USERNAMES:
        return {
          visibleFilters: [
            'email',
            'playerId',
            'telegramUsername',
            'affId',
            'registrationTimeFrom',
            'depositFrom',
            'depositTo',
          ],
        };

      case TableFilter.CF_ACCOUNTS:
        return { visibleFilters: ['cfAccountId', 'cfAccountCode'] };

      case TableFilter.TRANSLATION:
        return { visibleFilters: ['filterBy', 'filterValue'] };

      case TableFilter.BONUS_DEFINITION:
        statusItems = [
          { label: 'All Statuses', value: '' },
          ...Object.keys(BonusStatus)
            .filter((key) => BonusStatus[key as keyof typeof BonusStatus] !== BonusStatus.DELETED)
            .map((status) => ({
              label:
                BonusStatus[status as keyof typeof BonusStatus] === BonusStatus.DEACTIVE
                  ? 'Disabled'
                  : capitalizeString(status),
              value: status,
            })),
        ];
        bonusTypeItems = [
          { label: 'All Types', value: '' },
          ...Object.keys(BonusType).map((item) => ({
            label: capitalizeString(item),
            value: item,
          })),
        ];
        prizeTypeItems = [
          { label: 'All Types', value: '' },
          ...Object.keys(BonusPrizeType).map((type) => ({
            label: capitalizeString(type),
            value: type,
          })),
        ];
        visibilityMenuItemList = [
          { label: 'All', value: '' },
          { label: 'Hidden', value: 'HIDDEN' },
          { label: 'Visible', value: 'VISIBLE' },
        ];
        return {
          visibleFilters: ['bonusId', 'bonusDefinitionType', 'prizeType', 'status', 'visibility'],
          statusItems,
          bonusTypeItems,
          prizeTypeItems,
          visibilityMenuItemList,
        };

      default:
        return { visibleFilters: [], hiddenFilters: [], fromDateLabel: 'Opened At From' };
    }
  }, [tableFilter]);

  const [visibleFilters, setVisibleFilters] = React.useState<string[]>(initialFilterConfig.visibleFilters);

  React.useEffect(() => {
    const additionalFields = ['eventId', 'tourId', 'discipline'];
    const excludedFilters = ['gameTitle', 'originalProvider'];
    if (filters.betType?.includes('SPORT') && !filters.betType?.includes('CASINO')) {
      let updatedFilters = visibleFilters.filter((item) => !excludedFilters.includes(item));
      setVisibleFilters(updatedFilters);
      if (!visibleFilters.includes('betMode')) {
        updatedFilters.splice(4, 0, 'betMode');
        setVisibleFilters(updatedFilters);
      }
      if (filters.betMode === BetMode.SINGLE) {
        // Add additionalFields without duplicating them
        additionalFields.forEach((field) => {
          if (!updatedFilters.includes(field)) {
            updatedFilters.splice(5, 0, field);
          }
        });
        setVisibleFilters([...updatedFilters]);
      } else {
        // Remove additionalFields if betMode is not SINGLE
        updatedFilters = updatedFilters.filter((field) => !additionalFields.includes(field));
        setVisibleFilters([...updatedFilters]);
      }
    } else {
      const includedFilters = ['betMode', ...additionalFields];
      const updatedFilters = visibleFilters.filter((item) => !includedFilters.includes(item));
      setVisibleFilters(updatedFilters);
    }
  }, [filters.betType, filters.betMode]);

  const onSubmit = React.useCallback(
    async (inputValues: Values): Promise<void> => {
      const currentSearchParams = new URLSearchParams();

      visibleFilters.forEach((filter) => {
        const title = filter;
        const value = inputValues[filter as keyof Values];
        if (!value) return;

        if (title === 'hasImageUrl') {
          currentSearchParams.append(title, `${!value}`);
        } else {
          currentSearchParams.append(title, value);
        }

        if (title === 'betGameId') {
          currentSearchParams.set('betGameId', `${filters['betGameId']?.split(',')[0]}`);
          if (filters['betGameId']?.split(',')?.[1]) {
            currentSearchParams.set('gameTitle', filters['betGameId']?.split(',')[1]);
          } else if (filters['gameTitle']) currentSearchParams.set('gameTitle', filters['gameTitle']);
        }

        if (Array.isArray(value)) {
          // Filter out empty strings
          const filteredValue = value.filter((tag) => tag.trim() !== '');

          if (!filteredValue.length) {
            currentSearchParams.delete(title);
          }
        }
      });

      //keep basic filters in new URL

      const limitParam = searchParams.get('limit');
      const opIdParam = searchParams.get('opId');
      const parentOpIdParam = searchParams.get('parentOpId');
      const includeBrandsParam = searchParams.get('includeBrands');
      if (limitParam) currentSearchParams.set('limit', limitParam);
      if (opIdParam) currentSearchParams.set('opId', opIdParam);
      if (parentOpIdParam) currentSearchParams.set('parentOpId', parentOpIdParam);
      if (includeBrandsParam) currentSearchParams.set('includeBrands', includeBrandsParam);

      const currentUrl = new URL(window.location.href);
      currentUrl.search = currentSearchParams.toString();

      router.push(currentUrl.toString());

      dispatch(hideDrawerState(GeneralDrawer.TABLE_FILTERS));

      try {
        // Make API request
        // toast.success('Customer updated');
        // router.push(paths.dashboard.customers.details('1'));
      } catch (err) {
        // logger.error(err);
        // toast.error('Something went wrong!');
      }
    },
    [router]
  );

  // const fields: FieldType<zod.infer<typeof schema>>[] = [
  //   { name: 'playerId', label: 'Player ID', type: InputType.INPUT },
  //   { name: 'email', label: 'Email', type: InputType.INPUT },
  //   {
  //     name: 'filterBy',
  //     label: 'Filter By',
  //     type: InputType.SELECT,
  //     menuItems: [
  //       { label: 'Row Key', value: 'rowKey' },
  //       ...Object.entries(languages).map(([key, value]) => ({
  //         label: value.label,
  //         value: key,
  //       })),
  //     ],
  //   },
  //   { name: 'affId', label: 'Affiliate ID', type: InputType.INPUT },
  //   { name: 'filterValue', label: 'Filter Value', type: InputType.INPUT },
  //   { name: 'cfAccountId', label: 'CF Account ID', type: InputType.INPUT },
  //   { name: 'cfAccountCode', label: 'CF Account Code', type: InputType.INPUT },
  //   { name: 'bonusId', label: 'Bonus ID', type: InputType.INPUT },
  //   {
  //     name: 'status',
  //     label: 'Status',
  //     type: InputType.SELECT,
  //     menuItems: initialFilterConfig.statusItems || [],
  //   },
  //   {
  //     name: 'bonusDefinitionType',
  //     label: 'Bonus Type',
  //     type: InputType.SELECT,
  //     menuItems: initialFilterConfig.bonusTypeItems || [],
  //   },
  //   {
  //     name: 'prizeType',
  //     label: 'Prize Type',
  //     type: InputType.SELECT,
  //     menuItems: initialFilterConfig.prizeTypeItems || [],
  //   },
  //   {
  //     name: 'visibility',
  //     label: 'Visibility',
  //     type: InputType.SELECT,
  //     menuItems: initialFilterConfig.visibilityMenuItemList || [],
  //   },
  //   {
  //     name: 'telegramUsername',
  //     label: 'Telegram Username',
  //     type: InputType.INPUT,
  //   },
  //   {
  //     name: 'registrationTimeFrom',
  //     label: 'Joined At From Date',
  //     type: InputType.DATE_PICKER,
  //   },
  // ];

  const handleResetFilters = (): void => {
    reset(defaultValues);
    setVisibleFilters(initialFilterConfig.visibleFilters);
  };

  const filterSections: {
    title: string | undefined;
    fields: FieldType<zod.infer<typeof schema>>[];
  }[] = [
    {
      title: 'Player',
      fields: [
        { name: 'playerId', label: 'Player ID', type: InputType.INPUT },
        { name: 'email', label: 'Email', type: InputType.INPUT },
        { name: 'telegramUsername', label: 'Telegram Username', type: InputType.INPUT },
        { name: 'affId', label: 'Affiliate ID', type: InputType.INPUT },
        {
          name: 'registrationTimeFrom',
          label: 'Joined At From Date',
          type: InputType.DATE_PICKER,
        },
      ],
    },
    {
      title: 'Deposit',
      fields: [
        { name: 'depositFrom', label: 'From', type: InputType.NUMBER },
        { name: 'depositTo', label: 'To', type: InputType.NUMBER },
      ],
    },
    {
      title: 'CF Accounts',
      fields: [
        { name: 'cfAccountId', label: 'CF Account ID', type: InputType.INPUT },
        { name: 'cfAccountCode', label: 'CF Account Code', type: InputType.INPUT },
      ],
    },
    {
      title: 'Bonus Info',
      fields: [
        { name: 'bonusId', label: 'Bonus ID', type: InputType.INPUT },
        {
          name: 'bonusDefinitionType',
          label: 'Bonus Type',
          type: InputType.SELECT,
          menuItems: initialFilterConfig.bonusTypeItems || [],
        },
        {
          name: 'prizeType',
          label: 'Prize Type',
          type: InputType.SELECT,
          menuItems: initialFilterConfig.prizeTypeItems || [],
        },
        {
          name: 'status',
          label: 'Status',
          type: InputType.SELECT,
          menuItems: initialFilterConfig.statusItems || [],
        },
        {
          name: 'visibility',
          label: 'Visibility',
          type: InputType.SELECT,
          menuItems: initialFilterConfig.visibilityMenuItemList || [],
        },
      ],
    },
    {
      title: 'Translation',
      fields: [
        {
          name: 'filterBy',
          label: 'Filter By',
          type: InputType.SELECT,
          menuItems: [
            { label: 'Row Key', value: 'rowKey' },
            ...Object.entries(languages).map(([key, value]) => ({
              label: value.label,
              value: key,
            })),
          ],
        },
        { name: 'filterValue', label: 'Filter Value', type: InputType.INPUT },
      ],
    },
  ];

  // function renderVisibleFields(): FieldType<zod.infer<typeof schema>>[] {
  //   return fields.filter((field) => visibleFilters.includes(field.name));
  // }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={2}>
        {/* At least one bonus field should exist in the filters array for the section to be rendered  */}
        {filterSections.map((section) => {
          const visibleSectionFields = section.fields.filter((field) => visibleFilters.includes(field.name));

          if (!visibleSectionFields.length) return null;

          return (
            <Accordion defaultExpanded key={section.title}>
              <AccordionSummary expandIcon={<CaretDown />} aria-controls="panel1-content" id="panel1-header">
                <Typography component="span">{section.title}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={1}>
                  {visibleSectionFields.map((fieldInput) => (
                    <CustomConfigInput
                      key={fieldInput.name}
                      fieldType={{
                        ...fieldInput,
                        menuItems: fieldInput.menuItems ?? [],
                      }}
                      schema={schema}
                      control={control}
                    />
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          );
        })}
        <Stack direction="row" sx={{ gap: 2, mt: 4 }}>
          <Button variant="contained" color="warning" fullWidth onClick={handleResetFilters}>
            Reset
          </Button>
          <Button type="submit" variant="contained" fullWidth>
            Filter
          </Button>
        </Stack>
        <Button variant="contained" color="success" fullWidth onClick={handleResetFilters}>
          Create Group
        </Button>
      </Stack>
      {/* {renderVisibleFields().map((fieldInput) => (
        <CustomConfigInput
          key={fieldInput.name}
          fieldType={{
            ...fieldInput,
            menuItems: fieldInput.menuItems,
          }}
          schema={schema}
          control={control}
        />
      ))} */}
    </form>
  );
}

export default TableFilters;
